import * as React from 'react';

import { AttributeTableItem } from 'src/containers/ProjectDiagram/components/attributesTable/types';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { addAttribute } from 'src/containers/ProjectDiagram/utils/modelConceptAttribute/addAttribute';
import { addAttributeToSensitivePropertyGroup } from 'src/containers/ProjectDiagram/utils/sensitivePropertyGroups/addAttributeToSensitivePropertyGroup';
import { removeAttributeFromSensitivePropertyGroup } from 'src/containers/ProjectDiagram/utils/sensitivePropertyGroups/removeAttributeFromSensitivePropertyGroup';
import { removeSensitivePropertyGroup } from 'src/containers/ProjectDiagram/utils/sensitivePropertyGroups/removeSensitivePropertyGroup';
import { updateSensitivePropertyGroupName } from 'src/containers/ProjectDiagram/utils/sensitivePropertyGroups/updateSensitivePropertyGroupName';
import {
  SensitivePropertyGroupInfo,
  SensitivePropertyGroupsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { databaseSensitivePropertyGroupsSelector } from 'src/selectors/databaseSensitivePropertyGroups';
import { projectSensitivePropertyGroupNamesSelector } from 'src/selectors/projectSensitivePropertyGroupNames';
import { projectTargetModelIdSelector } from 'src/selectors/projectTargetModelId';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

// SPGs

export const useRemoveSensitivePropertyGroup = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();

  return React.useCallback(
    (groupName: string) => {
      if (!diagram) {
        return;
      }

      dispatch(
        uiSlice.actions.removedKeysFromEntityDrawerHistory({
          projectId,
          entityValues: [groupName],
        })
      );

      commitDiagramModel(diagram, removeSensitivePropertyGroup(groupName));
    },
    [diagram, dispatch, projectId]
  );
};

export const useValidateSensitivePropertyGroupName = () => {
  const { projectId } = useRouterParams();
  const sensitivePropertyGroups = useReduxSelector(
    projectSensitivePropertyGroupNamesSelector(projectId)
  );

  return React.useCallback(
    (name: string) => {
      if (!name) {
        return copy.components.projectDiagram.sensitivePropertyGroupDrawer
          .validation.name.required;
      }

      const existingNames = new Set(sensitivePropertyGroups);
      if (existingNames.has(name)) {
        return copy.components.projectDiagram.sensitivePropertyGroupDrawer
          .validation.name.unique;
      }

      if (name.includes('=')) {
        return copy.components.projectDiagram.sensitivePropertyGroupDrawer.validation.name.characters(
          ['=']
        );
      }

      return '';
    },
    [sensitivePropertyGroups]
  );
};

export const useValidateSensitivePropertyGroupPublishName = (
  databaseId: string | null,

  sensitivePropertyGroups: SensitivePropertyGroupsInfo
) => {
  const databaseSensitivePropertyGroupsByName = useReduxSelector(
    databaseSensitivePropertyGroupsSelector(databaseId)
  );

  return React.useCallback(
    (sensitivePropertyGroup: SensitivePropertyGroupInfo): string | null => {
      const trimmedName = sensitivePropertyGroup.publishName.trim();
      // empty name
      if (!trimmedName) {
        return copy.components.publishProjectDrawer.modelConfiguration
          .sensitivePropertyGroups.newPlaceholder;
      }
      // name already exists on database
      if (databaseSensitivePropertyGroupsByName[trimmedName]) {
        return copy.components.publishProjectDrawer.modelConfiguration.sensitivePropertyGroups.duplicateGroup(
          trimmedName
        );
      }
      // name exists in new to publish groups
      if (
        trimmedName !== sensitivePropertyGroup.spgName &&
        Object.values(sensitivePropertyGroups).find(
          (group) =>
            group.publishName === trimmedName &&
            group.spgName !== sensitivePropertyGroup.spgName
        )
      ) {
        return copy.components.publishProjectDrawer.modelConfiguration.sensitivePropertyGroups.duplicatePublishGroup(
          trimmedName
        );
      }
      // name cannot include =
      if (trimmedName.includes('=')) {
        return copy.components.projectDiagram.sensitivePropertyGroupDrawer.validation.name.characters(
          ['=']
        );
      }

      return null;
    },
    [databaseSensitivePropertyGroupsByName, sensitivePropertyGroups]
  );
};

export const useUpdateSensitivePropertyGroupName = (groupName: string) => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const diagram = useDiagram();
  return React.useCallback(
    (newGroupName: string) => {
      if (!diagram) {
        return;
      }

      dispatch(
        uiSlice.actions.updatedEntityDrawerValue({
          projectId,
          prevEntityValue: groupName,
          entityValue: newGroupName,
        })
      );

      commitDiagramModel(
        diagram,
        updateSensitivePropertyGroupName(groupName, newGroupName)
      );
    },
    [diagram, dispatch, groupName, projectId]
  );
};

// attributes

export const useAddAttributeToSensitivePropertyGroup = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const targetModelId = useReduxSelector(
    projectTargetModelIdSelector(projectId)
  );

  return React.useCallback(
    (groupName: string, attribute: AttributeTableItem) => {
      if (!diagram) {
        return;
      }

      if (attribute.isNewlyCreated && !attribute.text) {
        return;
      }

      commitDiagramModel(diagram, (model) => {
        if (attribute.isNewlyCreated) {
          addAttribute({
            ...attribute,
            projectModelIds: targetModelId ? [targetModelId] : [],
          })(model);
        }
        addAttributeToSensitivePropertyGroup(groupName, attribute.value)(model);
      });
    },
    [diagram, targetModelId]
  );
};

export const useRemoveAttributeFromSensitivePropertyGroup = () => {
  const diagram = useDiagram();
  return React.useCallback(
    (groupName: string, attribute: AttributeTableItem) => {
      if (!diagram) {
        return;
      }
      commitDiagramModel(
        diagram,
        removeAttributeFromSensitivePropertyGroup(groupName, attribute.value)
      );
    },
    [diagram]
  );
};
